import firebase from 'firebase/app';
import { Collection } from 'firestorter';

import callRemote from '../utils/remote';
import { firestorePaths } from '../utils/paths';
import {
  BREVO_AUTH_COOKIE_HEADER,
  getBrevoAuthCookie,
  throwIfBrevoSessionUnavailable,
} from '../CoachTools/pages/MassTextOutreach/utils/brevoAuth';
import BaseDocument from './BaseDocument';

const SEND_LEAD_CAMPAIGN_PATH = 'leadCampaign-sendLeadCampaign';
const HISTORY_DEFAULT_LIMIT = 100;

const LeadCampaignStatus = {
  CREATED: 'CREATED',
  SCHEDULED: 'SCHEDULED',
  IN_PROGRESS: 'IN_PROGRESS',
  SUCCESSFUL: 'SUCCESSFUL',
  PARTIALLY_COMPLETED: 'PARTIALLY_COMPLETED',
  FAILED: 'FAILED',
  WINDOW_MISSED: 'WINDOW_MISSED',
  SUSPENDED: 'SUSPENDED',
};

/**
 * Statuses where the campaign has not finished, so its totals are not final. The server writes them
 * once, when the campaign closes, so until then they read 0 rather than "not yet counted".
 *
 * Deliberately not the server's `IN_FLIGHT_STATUSES`, which omits `CREATED`: the two answer
 * different questions. This one asks whether these totals can be shown; the server's asks whether
 * `campaignDataSync` should try to close the campaign, and a campaign nobody has built yet has
 * nothing to close.
 */
const IN_FLIGHT_STATUSES = [
  LeadCampaignStatus.CREATED,
  LeadCampaignStatus.SCHEDULED,
  LeadCampaignStatus.IN_PROGRESS,
];

const LeadCampaignSource = {
  MANUAL: 'MANUAL',
  REENGAGEMENT_SEQUENCE: 'REENGAGEMENT_SEQUENCE',
};

const toIsoString = (value) => {
  if (!value) return null;
  if (value instanceof Date) return value.toISOString();
  if (typeof value.toDate === 'function') return value.toDate().toISOString();
  return new Date(value).toISOString();
};

class MassTextCampaign extends BaseDocument {
  get name() {
    return this.data.name;
  }

  get coachId() {
    return this.data.coachId;
  }

  get createdBy() {
    return this.data.createdBy;
  }

  get createdAt() {
    return this.data.createdAt;
  }

  get status() {
    return this.data.status;
  }

  get sequenceId() {
    return this.data.sequenceId;
  }

  get source() {
    return this.data.source;
  }

  static isReengagement(doc) {
    return doc?.source === LeadCampaignSource.REENGAGEMENT_SEQUENCE;
  }

  get totals() {
    return this.data.totals;
  }

  /** Bounced, plus recipients Brevo skipped or that never imported — what the Failed tab lists. */
  static getFailedTotal(totals) {
    if (!totals) return 0;
    return (totals.hardBounces || 0) + (totals.softBounces || 0) + (totals.failed || 0);
  }

  static isInFlight(status) {
    return IN_FLIGHT_STATUSES.includes(status);
  }

  static async send({
    name,
    coachId,
    body,
    audience,
    dpl,
    media,
  }) {
    const idToken = await firebase.auth().currentUser.getIdToken();

    const payload = {
      name,
      coachId,
      bodyTemplate: body,
      dpl: dpl.url,
      filterSnapshot: {
        audienceType: audience.audienceType,
        startDate: toIsoString(audience.startDate),
        endDate: toIsoString(audience.endDate),
        countries: audience.countries,
        salesStages: audience.salesStages,
        requireConsent: audience.requireConsent,
      },
      ...(media ? { media } : {}),
    };

    const response = await callRemote(idToken, SEND_LEAD_CAMPAIGN_PATH, payload, {
      headers: { [BREVO_AUTH_COOKIE_HEADER]: getBrevoAuthCookie() },
    });

    throwIfBrevoSessionUnavailable(response);

    if (!response.ok) {
      const failure = await response.json().catch(() => ({}));
      const error = new Error(failure.message || `Send failed (${response.status})`);
      error.step = failure.step;
      throw error;
    }

    return response.json();
  }

  static async getRecentBySource(source, limit = HISTORY_DEFAULT_LIMIT) {
    const collection = new Collection(firestorePaths.LEAD_CAMPAIGN, {
      createDocument: (src, opts) => new MassTextCampaign(src, { ...opts, disableObserverRefCount: true }),
      query: (ref) => ref.where('source', '==', source).orderBy('createdAt', 'desc').limit(limit),
    });
    await MassTextCampaign.initCollection(collection);
    return collection;
  }
}

export default MassTextCampaign;
export { LeadCampaignStatus, LeadCampaignSource };
